import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import { REG_HUB_COMMON_CONFIG } from '../reg-hub-common-config';

/**
 * Adds ?culture=<en|fr> (the active UI language) to every request sent to the RegHub APIs
 * configured in UiCommonModule.forRoot (rootUrl, batchRootURL, consortiumRootURL).
 * Other hosts (identity server, third-party APIs) are left untouched.
 * Idempotent: skips requests that already carry a culture param (the interceptor may be
 * registered more than once when feature modules also import UiCommonModule.forRoot).
 */
@Injectable()
export class CultureInterceptor implements HttpInterceptor {
  private readonly transloco = inject(TranslocoService, { optional: true });
  private readonly config = inject(REG_HUB_COMMON_CONFIG, { optional: true });

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const culture = this.transloco?.getActiveLang();
    if (!culture || !this.isApiRequest(req.url) || req.params.has('culture') || /[?&]culture=/i.test(req.url)) {
      return next.handle(req);
    }
    return next.handle(req.clone({ params: req.params.set('culture', culture) }));
  }

  private isApiRequest(url: string): boolean {
    const target = url.toLowerCase();
    return [this.config?.rootUrl, this.config?.batchRootURL, this.config?.consortiumRootURL]
      .filter((base): base is string => !!base)
      .some(base => target.startsWith(base.toLowerCase()));
  }
}
